'use strict';

/**
 * B2/B4 pre-cutover — browser-side JIT decrypt-grant MINT surface.
 *
 * Thin wrapper over createJitGrant + deriveIdentityKeypair, exported on
 * window.OysterVault (shared/crypto/browser-bundle-entry.js) so the browser
 * can finally MINT the grant the JIT_GRANT_ENFORCEMENT=1 gate demands
 * (auth/require-jit-grant.js — `Authorization: JIT <token>`). Until this
 * existed, the flag's ON-path could only ever refuse: nothing browser-side
 * produced a grant.
 *
 * KEY-PROVIDER SEAM (custody boundary — read before "improving" this):
 *   WHERE the signing capability lives is RESOLVED (2026-07-31, USER-authorised):
 *   B4 LD-8 option (b) — a NON-EXTRACTABLE WebCrypto Ed25519 session signer
 *   (shared/crypto/jit-signer.js) derived at unlock, held on the in-memory
 *   vault session, passed here as the `signer` option by the shared jitFetch
 *   wrapper (shared/crypto/jit-fetch.js). This module still ACCEPTS key
 *   material per call and holds NO state:
 *     - no module-level key variable, no cache, no global, no persistence;
 *     - caller-supplied material is never mutated or zeroized (the caller
 *       owns it); only the seed WE derive from a masterKey is zeroized,
 *       via the identity-key zeroization seam, the moment signing completes;
 *     - a keyless call throws typed ERR_JIT_KEY_MATERIAL_REQUIRED — it must
 *       NEVER fall back to createJitGrant's ephemeral test/mock keypair,
 *       which would mint a well-formed grant the server refuses with
 *       publickey_mismatch (a silent-failure trap).
 *
 * WHICH KEY (load-bearing — the trap that caught a prior lane): real grants
 * are signed by deriveIdentityKeypair(masterKey) (HKDF label
 * "oyster:identity:ed25519:v1") — the SAME derivation
 * auth/master-identity-registration.js registers server-side as
 * user.pubKey.ed25519 on warm establish, and the one
 * auth/jit-content-gate.js makeMasterPubKeyLookup cross-checks the grant's
 * embedded key against. The identity-vault `publicKeys.sign.ed25519` is an
 * independently-random keypair; signing with it verifies standalone but the
 * bound validator refuses every such grant.
 *
 * Browser compat: createJitGrant's `require('node:crypto')`
 * (randomBytes/randomUUID) is aliased to scripts/esbuild-crypto-shim.js in
 * the vault-bundle build — both stay CSPRNG-backed (Web Crypto).
 */

const { createJitGrant } = require('./jit-grant.js');
const { deriveIdentityKeypair, zeroizeKey } = require('./identity-key.js');

function typedError(code, message) {
  const err = new Error(message);
  err.code = code;
  return err;
}

/**
 * Mint a signed JIT decrypt-grant from caller-supplied key material.
 *
 * Exactly ONE of masterKey / signingKey / signer must be provided:
 *   - masterKey:  32-byte Uint8Array — the identity signing seed is derived
 *                 here (deriveIdentityKeypair) and zeroized after signing.
 *   - signingKey: 32-byte Ed25519 seed already derived by the caller (B4's
 *                 sign-only helper shape). NOT zeroized — the caller owns it.
 *   - signer:     the sign-only session signer (shared/crypto/jit-signer.js —
 *                 { sign(bytes), publicKey }). NO raw key material crosses
 *                 this seam at all: signing happens behind the signer's
 *                 non-extractable boundary. This is the resolved B4 custody
 *                 shape (LD-8 option (b)) — the form the shared jitFetch
 *                 wrapper uses.
 *
 * Remaining options (userId, dataPath, expiresAt, aud, actor, verb, scopes,
 * consentMode) pass through to createJitGrant unchanged.
 *
 * @param {object} opts — { masterKey | signingKey | signer, userId, dataPath, ... }
 * @returns {Promise<object>} flat signed grant (body + signature + publicKey)
 */
async function mintJitGrant({ masterKey, signingKey, signer, ...grantOpts } = {}) {
  const provided = [masterKey, signingKey, signer].filter((v) => v != null).length;
  if (provided > 1) {
    throw typedError('ERR_JIT_KEY_MATERIAL_AMBIGUOUS',
      'mintJitGrant: pass exactly one of masterKey / signingKey / signer');
  }
  if (provided === 0) {
    throw typedError('ERR_JIT_KEY_MATERIAL_REQUIRED',
      'mintJitGrant: key material required — pass masterKey (32-byte Uint8Array), '
      + 'signingKey, or signer. This surface never stores, caches, or falls back '
      + 'to an ephemeral key.');
  }

  if (signer != null) {
    // Sign-only capability: the key never exists in this scope.
    return createJitGrant({ ...grantOpts, signer });
  }

  if (signingKey != null) {
    // Caller-derived seed: borrow it for the call, never mutate it.
    return createJitGrant({ ...grantOpts, signingKey });
  }

  // masterKey path: derive the identity seed here, zeroize it the moment the
  // signing operation completes (identity-key.js zeroization seam). The
  // caller's masterKey itself is untouched.
  const kp = deriveIdentityKeypair(masterKey);
  try {
    return await createJitGrant({ ...grantOpts, signingKey: kp.privateKey });
  } finally {
    zeroizeKey(kp.privateKey);
  }
}

/**
 * Encode a flat grant object into the `Authorization: JIT <token>` token form
 * — base64 of the grant JSON, exactly what auth/require-jit-grant.js
 * decodeGrantToken() reverses. Pure encoding: no key material involved.
 *
 * @param {object} grant — flat signed grant from mintJitGrant
 * @returns {string} base64 token (caller prefixes "JIT " in the header)
 */
function encodeJitGrantToken(grant) {
  if (!grant || typeof grant !== 'object' || Array.isArray(grant)) {
    throw new Error('encodeJitGrantToken: grant must be the flat grant object');
  }
  return Buffer.from(JSON.stringify(grant), 'utf8').toString('base64');
}

module.exports = { mintJitGrant, encodeJitGrantToken };
